import React, { useState, useMemo, useEffect } from "react";
import clsx from "clsx";
import capitalize from "@utils/capitalize";
import { Icon } from "@atoms";
import { m } from "framer-motion";
import Fuse from "fuse.js";
import { HtmlParser } from "@base";

const FilterSelect = ({
  attribute,
  options,
  state,
  // expanded: _expanded,
  childCollections,
  initial,
}) => {
  const [expanded, setExpanded] = useState(initial);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const search = useMemo(() => {
    const fuse = new Fuse(
      options.map(([k, v]) => ({ key: k, value: v })),
      { keys: ["key"], threshold: 0.3, ignoreLocation: true }
    );
    return fuse;
  }, []);

  const [filters, setFilters] = state;
  const label =
    {
      application: "Client Type",
      productLine: "Product Line",
      pinyin: "Formula",
      type: "Tablet/Extract",
      organic: "Organic Content",
    }[attribute] || capitalize(attribute);

  useEffect(() => {
    const res = search.search(query);
    const processed = res.map(({ item: { key, value } }) => [key, value]);
    setResults(processed);
  }, [query]);

  const sortFunc = ([, avalue], [, bvalue]) => {
    if (filters?.includes(avalue) && filters?.includes(bvalue)) return -1;
    if (!filters?.includes(avalue) && filters?.includes(bvalue)) return 1;
    return 0;
  };

  return (
    <div className="rounded border border-orange bg-white p-2 px-3 sm:p-4">
      <button
        type="button"
        onClick={() => setExpanded(s => !s)}
        className="flex w-full items-center justify-between text-left font-bold leading-none"
      >
        <span className="w-full text-sm sm:text-base">{label}</span>
        <span className="block">
          <Icon
            name="triangle"
            className={clsx("h-5 w-5 text-orange transition duration-300", {
              "rotate-180": !expanded,
            })}
          />
        </span>
      </button>
      <m.div
        initial={{ height: 0 }}
        className="overflow-hidden"
        animate={expanded ? { height: "auto" } : { height: 0 }}
      >
        {options.length > 5 && (
          <div
            className={clsx(
              "relative mb-2 flex w-full items-center overflow-hidden border-b border-orange bg-white p-5 text-sm transition duration-300"
            )}
          >
            <input
              onChange={e => setQuery(e.target.value)}
              placeholder="Search"
              className={clsx(
                "absolute inset-0 pb-1 pr-8 pt-5 placeholder:text-black"
              )}
            />
            <Icon
              name="search"
              className="pointer-events-none absolute bottom-0 right-1 top-4 z-10 h-4 w-4"
            />
          </div>
        )}
        <ul className="flex max-h-[20rem] flex-col gap-2 overflow-auto pb-1 pt-3">
          {(!results.length ? options.sort(sortFunc) : results).map(
            ([title, value]) => (
              <li className="relative" key={`${attribute}--${value}`}>
                <button
                  onClick={() =>
                    setFilters(s => ({
                      ...s,
                      [attribute]: !s[attribute].includes(value)
                        ? [...s[attribute], value]
                        : s[attribute].filter(item => item !== value),
                    }))
                  }
                  type="button"
                  className="flex w-full items-baseline gap-2 text-left"
                >
                  <span className="sr-only">close</span>
                  <span
                    className={clsx(
                      "relative top-[0.1rem] flex h-4 w-4 flex-shrink-0 items-center justify-center border border-orange transition duration-300",
                      {
                        "bg-orange": filters.includes(value),
                      }
                    )}
                  >
                    <Icon name="close" className="h-4 w-4 text-white" />
                  </span>
                  <span className="text-sm">
                    <HtmlParser html={title} />
                  </span>
                </button>
                {filters.includes(value) && childCollections?.[value] && (
                  <ul className="grid grid-cols-1 gap-1">
                    {childCollections[value]
                      ?.sort(sortFunc)
                      .map(([ctitle, cvalue]) => (
                        <li className="relative" key={cvalue}>
                          <button
                            onClick={() =>
                              setFilters(s => ({
                                ...s,
                                [attribute]: !s[attribute].includes(cvalue)
                                  ? [...s[attribute], cvalue]
                                  : s[attribute].filter(
                                      item => item !== cvalue
                                    ),
                              }))
                            }
                            type="button"
                            className="flex w-full items-center gap-2 text-left"
                          >
                            <span className="sr-only">close</span>
                            <span
                              className={clsx(
                                "flex h-3 w-3 shrink-0 items-center justify-center border border-orange transition duration-300",
                                {
                                  "bg-orange": filters.includes(cvalue),
                                }
                              )}
                            >
                              <Icon
                                name="close"
                                className="h-3 w-3 text-white"
                              />
                            </span>
                            <span className="text-sm">
                              <HtmlParser html={ctitle} />
                            </span>
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            )
          )}
        </ul>
      </m.div>
    </div>
  );
};

export default FilterSelect;
